import {
  COLOR_ACTION_LABELS,
  COLOR_BY_CAMP_PRESET,
  type ColorRule,
  describeCondition,
  ENTITY_KIND_LABELS,
  ENTITY_KINDS,
  type EntityStyle,
  evaluateRules,
  NO_RULE_MATCHED,
  type SchemaSnapshot,
  whyText,
} from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Palette, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ColorLegend } from '../components/colors/color-by';
import { type DraftRule, emptyRule, RuleForm } from '../components/colors/rule-editor';
import { DataText } from '../components/data/data-text';
import { EntityMark } from '../components/data/entity-mark';
import { EmptyState } from '../components/guidance/empty-state';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Swatch, slotName } from '../components/schema/color-slot-picker';
import { EditorError, Panel } from '../components/schema/editor-kit';
import { Button } from '../components/ui/button';
import { useOptionalConfirm } from '../components/ui/confirm';
import { useColorMode, useColorRuleEdit, useColorRules, usePreviewSample } from '../lib/colors';
import { shapeOf } from '../lib/entities';
import { useSchema } from '../lib/schema';

// Admin → Color rules (PLAN §12.2, §12.7): the ordered rule list (first match wins), the rule form, and a live
// preview that evaluates the draft with the same code the server caches, on the most recent entities.

export const Route = createFileRoute('/admin/colors')({ component: ColorRulesPage });

const TOUR = {
  id: 'admin-colors',
  steps: [
    {
      target: '[data-tour="rules-list"]',
      title: 'Rules, in order',
      body: 'The first matching rule sets an entity’s color; fade and ring rules apply on top. Move rules up or down to change which wins.',
      learnMore: 'color-rules',
    },
    {
      target: '[data-tour="rules-preview"]',
      title: 'Live preview',
      body: 'While you edit a rule, recent entities show the colors they would get. Nothing changes until you save.',
    },
  ],
};

type Editing = { draft: DraftRule } | null;

function ColorRulesPage() {
  useScreenTour(TOUR);
  const { data: snap } = useSchema();
  const rules = useColorRules();
  const edit = useColorRuleEdit();
  const confirm = useOptionalConfirm();
  const { mode, setMode } = useColorMode();
  const [editing, setEditing] = useState<Editing>(null);
  if (!snap || !rules.data) return <PageHeader title="Color rules" description="Loading…" />;
  const list = rules.data.rules;
  const hasCampPreset = list.some(r => r.colorFromField === 'camp');
  // The rules as they would be with the draft saved: the preview evaluates these.
  const draftRules: ColorRule[] = editing
    ? editing.draft.id
      ? list.map(r => (r.id === editing.draft.id ? { ...r, ...editing.draft, id: r.id } : r))
      : [...list, { ...editing.draft, id: 'draft', order: list.length, updatedAt: 0 }]
    : list;
  const move = (i: number, d: number) => {
    const ids = list.map(r => r.id);
    const [id] = ids.splice(i, 1);
    if (id) ids.splice(i + d, 0, id);
    edit.mutate({ path: '/admin/color-rules/order', method: 'PUT', body: { ids } });
  };
  const save = () => {
    if (!editing) return;
    const { id, ...body } = editing.draft;
    edit.mutate(id ? { path: `/admin/color-rules/${id}`, method: 'PATCH', body } : { path: '/admin/color-rules', body }, {
      onSuccess: () => setEditing(null),
    });
  };
  return (
    <>
      <PageHeader
        title="Color rules"
        description="“Color by: Rules” colors entities everywhere — lists, pickers, profiles and graphs — by these rules. First match wins."
        actions={
          <Button size="sm" onClick={() => setEditing({ draft: emptyRule() })} disabled={!!editing}>
            <Plus aria-hidden="true" /> New rule
          </Button>
        }
      />
      <Tip id="admin-colors" learnMore="color-rules">
        Rules use the entity’s current values: type, fields such as Camp, tags, links (“linked to Actor X”, “donated &gt; 10,000”), review
        status, sensitivity and AI confidence. Hover any colored mark in the app to see which rule colored it.
      </Tip>
      {mode !== 'rules' && (
        <p className="mt-3 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm">
          You are viewing colors by type. Switch to rules to see these colors across the app.
          <Button size="sm" variant="outline" onClick={() => setMode('rules')}>
            Color by rules
          </Button>
        </p>
      )}
      <div className="mt-4 grid gap-6 xl:grid-cols-[1fr_22rem]">
        <div className="min-w-0 space-y-4">
          {editing && (
            <Panel title={editing.draft.id ? 'Edit rule' : 'New rule'}>
              <RuleForm
                snap={snap}
                draft={editing.draft}
                onDraft={d => setEditing({ draft: d })}
                onSave={save}
                onCancel={() => {
                  edit.clearError();
                  setEditing(null);
                }}
                saving={edit.isPending}
                error={edit.error}
              />
            </Panel>
          )}
          <Panel title="Rules">
            <div data-tour="rules-list">
              {list.length === 0 ? (
                <EmptyState icon={Palette} title="No color rules yet" learnMore="color-rules">
                  Add a rule, or start from the Color by Camp preset.
                </EmptyState>
              ) : (
                <ol className="divide-y rounded-md border" aria-label="Color rules">
                  {list.map((r, i) => (
                    <li key={r.id} className="flex flex-wrap items-center gap-2 p-2 text-sm" aria-label={`Rule ${i + 1}`}>
                      <span className="w-7 font-mono text-xs text-muted-foreground">#{i + 1}</span>
                      <RuleSwatch rule={r} snap={snap} />
                      <span className={`min-w-0 flex-1 ${r.enabled ? '' : 'text-muted-foreground line-through'}`}>
                        <span className="font-medium">{r.name ?? describeCondition(r.condition, snap)}</span>
                        {r.name && <span className="block text-xs text-muted-foreground">{describeCondition(r.condition, snap)}</span>}
                      </span>
                      <span className="rounded bg-muted px-1.5 text-xs">{COLOR_ACTION_LABELS[r.action]}</span>
                      <label className="flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={r.enabled}
                          onChange={e =>
                            edit.mutate({ path: `/admin/color-rules/${r.id}`, method: 'PATCH', body: { enabled: e.target.checked } })
                          }
                        />
                        Enabled
                      </label>
                      <span className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Move rule ${i + 1} up`}
                          disabled={i === 0}
                          onClick={() => move(i, -1)}
                        >
                          <ArrowUp aria-hidden="true" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Move rule ${i + 1} down`}
                          disabled={i === list.length - 1}
                          onClick={() => move(i, 1)}
                        >
                          <ArrowDown aria-hidden="true" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Edit rule ${i + 1}`}
                          disabled={!!editing}
                          onClick={() => setEditing({ draft: { ...r } })}
                        >
                          <Pencil aria-hidden="true" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Delete rule ${i + 1}`}
                          onClick={async () => {
                            const ok = confirm
                              ? await confirm({
                                  title: `Delete rule #${i + 1}?`,
                                  body: 'Entities it colored fall through to the next matching rule.',
                                  confirmLabel: 'Delete rule',
                                  destructive: true,
                                })
                              : true;
                            if (ok) edit.mutate({ path: `/admin/color-rules/${r.id}`, method: 'DELETE' });
                          }}
                        >
                          <Trash2 aria-hidden="true" />
                        </Button>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              {!hasCampPreset && (
                <Button
                  className="mt-3"
                  size="sm"
                  variant="outline"
                  onClick={() => edit.mutate({ path: '/admin/color-rules', body: COLOR_BY_CAMP_PRESET })}
                >
                  Add the Color by Camp preset
                </Button>
              )}
              {!editing && <EditorError error={edit.error} />}
            </div>
          </Panel>
        </div>
        <div data-tour="rules-preview">
          <Preview snap={snap} rules={draftRules} drafting={!!editing} />
        </div>
      </div>
      <p className="mt-4 text-sm text-muted-foreground">
        Colors are the entity palette; the colors of list values (Camp…) are set in{' '}
        <Link to="/admin/lists" search={{ list: 'camp' }} className="underline">
          Value lists
        </Link>
        .
      </p>
    </>
  );
}

function RuleSwatch({ rule, snap }: { rule: ColorRule; snap: SchemaSnapshot }) {
  if (rule.action === 'fade')
    return <span aria-hidden="true" className="inline-block size-3.5 rounded-full border border-foreground/40 opacity-40" />;
  if (rule.colorFromField) {
    const f = snap.fields.find(x => x.key === rule.colorFromField);
    const values = snap.lists.find(l => l.key === f?.enumList)?.values.filter(v => v.colorSlot && !v.archived) ?? [];
    return (
      <span className="flex -space-x-1" title={`Colors of ${f?.label ?? rule.colorFromField}`}>
        {values.slice(0, 5).map(v => (
          <Swatch key={v.id} slot={v.colorSlot} />
        ))}
      </span>
    );
  }
  return (
    <>
      <Swatch slot={rule.colorSlot} />
      <span className="sr-only">{slotName(rule.colorSlot)}</span>
    </>
  );
}

/** Live preview: the draft rules evaluated on recent entities, as they would look with the draft saved. */
function Preview({ snap, rules, drafting }: { snap: SchemaSnapshot; rules: ColorRule[]; drafting: boolean }) {
  const [kind, setKind] = useState('');
  const [onlyMatches, setOnlyMatches] = useState(false);
  const sample = usePreviewSample(kind);
  const rows = useMemo(() => {
    if (!sample.data) return [];
    const byId = new Map(sample.data.facts.map(f => [f.id, f]));
    return sample.data.entities.flatMap(e => {
      const f = byId.get(e.id);
      if (!f) return [];
      const next: EntityStyle = evaluateRules(rules, f, snap);
      const changed = !!e.style && whyText(e.style) !== whyText(next);
      return [{ e, style: next, changed }];
    });
  }, [sample.data, rules, snap]);
  const matched = rows.filter(r => r.style.why !== NO_RULE_MATCHED || r.style.fade || r.style.ring);
  const shown = onlyMatches ? matched : rows;
  return (
    <Panel title="Live preview">
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {sample.isLoading
          ? 'Loading recent entities…'
          : `${matched.length} of ${rows.length} recent entities match a rule${drafting ? ' (with your unsaved changes)' : ''}.`}
      </p>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <select
          aria-label="Preview kind"
          className="h-8 rounded-md border border-input bg-surface px-2 text-sm"
          value={kind}
          onChange={e => setKind(e.target.value)}
        >
          <option value="">All kinds</option>
          {ENTITY_KINDS.map(k => (
            <option key={k} value={k}>
              {ENTITY_KIND_LABELS[k]}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={onlyMatches} onChange={e => setOnlyMatches(e.target.checked)} /> Only matches
        </label>
      </div>
      {rows.length > 0 && <ColorLegend styles={rows.map(r => r.style)} />}
      <ul className="max-h-[28rem] space-y-1 overflow-auto" aria-label="Preview entities" data-testid="rules-preview">
        {shown.map(({ e, style, changed }) => (
          <li key={e.id} className="flex items-center gap-2 text-sm" data-preview-id={e.id} data-color-slot={style.colorSlot ?? 'none'}>
            <EntityMark
              kind={shapeOf(snap, e.typeId, e.kind)}
              colorSlot={style.colorSlot}
              status={e.reviewStatus}
              confidential={e.sensitivity === 'confidential'}
              why={whyText(style)}
              fade={style.fade}
              ring={style.ring}
              ringSlot={style.ringSlot}
            />
            <DataText className="min-w-0 flex-1 truncate">{e.displayName}</DataText>
            {changed && <span className="rounded bg-muted px-1 text-[10px] uppercase">changes</span>}
          </li>
        ))}
      </ul>
      {!sample.isLoading && rows.length === 0 && <p className="text-sm text-muted-foreground">No entities yet.</p>}
    </Panel>
  );
}
